/**
 * @file AttachmentsTab Component
 * @description Tab for managing investment file attachments.
 * Supports upload, download, edit, and delete operations with AI extraction status display.
 */

import {
  useAttachments,
  useUploadAttachment,
  useUpdateAttachment,
  useDeleteAttachment,
  useDownloadAttachment,
  attachmentKeys,
} from '@medsuite/api-client';
import {
  ALLOWED_MIME_TYPES,
  MAX_FILE_SIZE,
  type InvestmentAttachmentMeta,
  type ExtractionStatus,
  type Investment,
} from '@medsuite/models';
import { Button, Card, Input, Alert, Spinner } from '@medsuite/ui-components';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useRef, useCallback, useEffect } from 'react';

interface AttachmentsTabProps {
  investment: Investment;
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format date for display
 */
function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get file type icon based on MIME type
 */
function getFileIcon(mimeType: string): string {
  if (mimeType.includes('pdf')) {
    return '📄';
  }
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return '📝';
  }
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('csv')) {
    return '📊';
  }
  if (mimeType.includes('image')) {
    return '🖼️';
  }
  if (mimeType.includes('text/plain')) {
    return '📃';
  }
  return '📎';
}

/**
 * Get extraction status badge
 */
function ExtractionStatusBadge({ status }: { status: ExtractionStatus }) {
  const styles: Record<ExtractionStatus, { bg: string; text: string; label: string }> = {
    pending: {
      bg: 'bg-yellow-100 dark:bg-yellow-900',
      text: 'text-yellow-800 dark:text-yellow-200',
      label: 'Processing...',
    },
    processing: {
      bg: 'bg-blue-100 dark:bg-blue-900',
      text: 'text-blue-800 dark:text-blue-200',
      label: 'Extracting...',
    },
    completed: {
      bg: 'bg-green-100 dark:bg-green-900',
      text: 'text-green-800 dark:text-green-200',
      label: 'AI Readable',
    },
    failed: {
      bg: 'bg-red-100 dark:bg-red-900',
      text: 'text-red-800 dark:text-red-200',
      label: 'Extraction Failed',
    },
    unsupported: {
      bg: 'bg-gray-100 dark:bg-gray-800',
      text: 'text-gray-600 dark:text-gray-400',
      label: 'No Text Content',
    },
  };

  const style = styles[status];

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}
    >
      {status === 'processing' || status === 'pending' ? (
        <span className="mr-1 animate-spin">⏳</span>
      ) : status === 'completed' ? (
        <span className="mr-1">✓</span>
      ) : null}
      {style.label}
    </span>
  );
}

/**
 * Edit attachment modal
 */
interface EditModalProps {
  attachment: InvestmentAttachmentMeta;
  onSave: (title: string, description: string | null) => void;
  onClose: () => void;
  isSaving: boolean;
}

function EditModal({ attachment, onSave, onClose, isSaving }: EditModalProps) {
  const [title, setTitle] = useState(attachment.title);
  const [description, setDescription] = useState(attachment.description || '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Edit Attachment
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description (optional)"
              rows={3}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave(title, description || null)}
            disabled={isSaving || !title.trim()}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Delete confirmation modal
 */
interface DeleteModalProps {
  attachment: InvestmentAttachmentMeta;
  onConfirm: () => void;
  onClose: () => void;
  isDeleting: boolean;
}

function DeleteModal({ attachment, onConfirm, onClose, isDeleting }: DeleteModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Delete Attachment
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Are you sure you want to delete &ldquo;{attachment.title}&rdquo;? This action cannot be
          undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * AttachmentsTab Component
 */
export const AttachmentsTab: React.FC<AttachmentsTabProps> = ({ investment }) => {
  const investmentId = investment.investment_id;
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [editingAttachment, setEditingAttachment] = useState<InvestmentAttachmentMeta | null>(null);
  const [deletingAttachment, setDeletingAttachment] = useState<InvestmentAttachmentMeta | null>(
    null
  );

  // Queries and mutations
  const { data: attachments = [], isLoading } = useAttachments(investmentId);
  const uploadAttachment = useUploadAttachment(investmentId);
  const updateAttachment = useUpdateAttachment(investmentId);
  const deleteAttachment = useDeleteAttachment(investmentId);
  const downloadAttachment = useDownloadAttachment(investmentId);

  // Generate accepted file types for input
  const acceptedTypes = ALLOWED_MIME_TYPES.join(',');

  // Handle file selection
  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      // Reset error
      setUploadError(null);

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`File is too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB.`);
        return;
      }

      // Validate file type
      if (!(ALLOWED_MIME_TYPES as readonly string[]).includes(file.type)) {
        setUploadError(
          `File type "${file.type}" is not allowed. Allowed types: PDF, Word, Excel, CSV, Text, and Images.`
        );
        return;
      }

      try {
        await uploadAttachment.mutateAsync({
          file,
          title: file.name,
        });
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setUploadError(
          error instanceof Error ? error.message : 'Failed to upload file. Please try again.'
        );
      }
    },
    [uploadAttachment]
  );

  // Handle edit save
  const handleEditSave = useCallback(
    async (title: string, description: string | null) => {
      if (!editingAttachment) {
        return;
      }

      try {
        await updateAttachment.mutateAsync({
          attachmentId: editingAttachment.attachment_id,
          data: { title, description },
        });
        setEditingAttachment(null);
      } catch (error) {
        console.error('Failed to update attachment:', error);
      }
    },
    [editingAttachment, updateAttachment]
  );

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!deletingAttachment) {
      return;
    }

    try {
      await deleteAttachment.mutateAsync(deletingAttachment.attachment_id);
      setDeletingAttachment(null);
    } catch (error) {
      console.error('Failed to delete attachment:', error);
    }
  }, [deletingAttachment, deleteAttachment]);

  // Handle download
  const handleDownload = useCallback(
    async (attachment: InvestmentAttachmentMeta) => {
      try {
        await downloadAttachment.mutateAsync({
          attachmentId: attachment.attachment_id,
          filename: attachment.filename,
        });
      } catch (error) {
        console.error('Failed to download attachment:', error);
      }
    },
    [downloadAttachment]
  );

  // Refresh attachments periodically to check extraction status
  useEffect(() => {
    const hasPending = attachments.some(
      (a) => a.extraction_status === 'pending' || a.extraction_status === 'processing'
    );

    if (hasPending) {
      const interval = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: attachmentKeys.list(investmentId) });
      }, 3000);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [attachments, investmentId, queryClient]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Attachments
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Upload documents to support this investment. Files will be analyzed for AI
                assistant context.
              </p>
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptedTypes}
                onChange={handleFileSelect}
                className="hidden"
                id="attachment-upload"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadAttachment.isPending}
              >
                {uploadAttachment.isPending ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Uploading...
                  </>
                ) : (
                  '+ Add Attachment'
                )}
              </Button>
            </div>
          </div>

          {uploadError && (
            <Alert variant="error" className="mb-4">
              {uploadError}
            </Alert>
          )}

          <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Supported formats: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), CSV, Text, Images (PNG,
            JPEG, GIF). Max size: {MAX_FILE_SIZE / (1024 * 1024)}MB
          </div>

          {attachments.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <div className="text-4xl mb-2">📎</div>
              <p>No attachments yet.</p>
              <p className="text-sm">Upload documents to add context for the AI assistant.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      File
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      AI Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Uploaded
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {attachments.map((attachment) => (
                    <tr
                      key={attachment.attachment_id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">
                            {getFileIcon(attachment.mime_type)}
                          </span>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {attachment.title}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {attachment.filename}
                            </div>
                            {attachment.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {attachment.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {formatFileSize(attachment.file_size)}
                      </td>
                      <td className="px-4 py-4">
                        <ExtractionStatusBadge status={attachment.extraction_status} />
                        {attachment.extraction_error && (
                          <div className="text-xs text-red-500 mt-1" title={attachment.extraction_error}>
                            Error: {attachment.extraction_error.substring(0, 50)}...
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(attachment.uploaded_at)}
                        {attachment.uploaded_by && (
                          <div className="text-xs">{attachment.uploaded_by}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right text-sm">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(attachment)}
                            disabled={downloadAttachment.isPending}
                          >
                            Download
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingAttachment(attachment)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => setDeletingAttachment(attachment)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Edit Modal */}
      {editingAttachment && (
        <EditModal
          attachment={editingAttachment}
          onSave={handleEditSave}
          onClose={() => setEditingAttachment(null)}
          isSaving={updateAttachment.isPending}
        />
      )}

      {/* Delete Modal */}
      {deletingAttachment && (
        <DeleteModal
          attachment={deletingAttachment}
          onConfirm={handleDelete}
          onClose={() => setDeletingAttachment(null)}
          isDeleting={deleteAttachment.isPending}
        />
      )}
    </div>
  );
};

export default AttachmentsTab;
